import { useState } from "react";
import { MoreHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent } from "@/components/_atoms/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_atoms/dropdown-menu";
import { ArrowDownTrayIcon, ArrowTopRightOnSquareIcon } from "@heroicons/react/24/solid";
import { Row } from "@tanstack/react-table";

import { FileDeleteForm } from "../file-delete-form";
import { FileDownloader } from "../file-downloader";
import { FileUpdateForm } from "../file-update-form";

interface DatatableRowActionsProps<TData> {
  row: Row<TData>;
  file: {
    id: string;
    ownerId: string;
    ownerType: string;
    name: string;
  };
  isReadOnly: boolean;
}

export function DatatableRowActions<TData>({ file, isReadOnly }: DatatableRowActionsProps<TData>) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
  const { t } = useTranslation("common");

  return (
    <div className={"flex flex-row items-center gap-1"}>
      <FileDownloader file={file} mode="OPEN">
        <Button variant="ghost" size="icon" className="h-8 w-8 self-end p-0">
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          <span className="sr-only">{t("fileDatatable.open")}</span>
        </Button>
      </FileDownloader>
      <FileDownloader file={file} mode="DOWNLOAD">
        <Button variant="ghost" size="icon" className="h-8 w-8 self-end p-0">
          <ArrowDownTrayIcon className="h-4 w-4" />
          <span className="sr-only">{t("fileDatatable.download")}</span>
        </Button>
      </FileDownloader>

      {!isReadOnly && (
        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <div className="flex w-full flex-row items-center justify-end">
              <Button variant="ghost" size="icon" className="h-8 w-8 self-end p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </div>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <FileDownloader file={file} mode="OPEN">
              <DropdownMenuItem className="cursor-pointer">{t("fileDatatable.open")}</DropdownMenuItem>
            </FileDownloader>
            <FileDownloader file={file} mode="DOWNLOAD">
              <DropdownMenuItem className="cursor-pointer">{t("fileDatatable.download")}</DropdownMenuItem>
            </FileDownloader>

            <>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  setUpdateDialogOpen(true);
                }}
              >
                {t("fileDatatable.rename")}
              </DropdownMenuItem>

              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => {
                  setDeleteDialogOpen(true);
                }}
              >
                {t("fileDatatable.delete")}
              </DropdownMenuItem>
            </>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <FileDeleteForm
            file={file}
            onSuccess={() => {
              setDeleteDialogOpen(false);
            }}
            onCancel={() => {
              setDeleteDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={updateDialogOpen} onOpenChange={setUpdateDialogOpen}>
        <DialogContent>
          <FileUpdateForm
            file={file}
            onSuccess={() => {
              setUpdateDialogOpen(false);
            }}
            onCancel={() => {
              setUpdateDialogOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
